import { Flex, Form, message, Switch, TableColumnsType, Button, Modal } from 'antd';
import * as Component from 'components';
import {
  TransferDataHandlerInterface,
  TransferDataSourceInterface,
} from 'components/transfer/Transfer';
import { contentService } from 'content/service';
import { ContentInterface } from 'content/type';
import {
  VideoManifestUpdateResponseInterface,
  VideoManifestUpdateDetailInterface,
  UpdatedManifestInterface,
} from 'content/type/contentType';
import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { Link, SetURLSearchParams } from 'react-router-dom';
import { seasonContentService } from '../../services/seasonContentService';
import { SeasonContentInterface } from '../../types/seasonContentTypes';
import './Content.css';
import { AGE_RATING } from 'constants/constant';

interface ContentProps {
  seasonId: number;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

const Content: React.FC<ContentProps> = ({ seasonId, searchParams, setSearchParams }) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [isRetranscoding, setIsRetranscoding] = useState<boolean>(false);
  const [] = Form.useForm();

  const handleOnClickTitle = (id: number): void => {
    window.open(`/video/detail/${id}`, '_blank');
  };

  // 시즌의 모든 컨텐츠에 대해 비디오 URL 새로고침을 실행하는 함수
  const handleSeasonVideoUrlRefresh = async (): Promise<void> => {
    // 결과 모달을 표시하는 공통 함수
    const showResultModal = (
      results: VideoManifestUpdateDetailInterface[],
      totalSuccess: number,
      totalFailed: number
    ) => {
      Modal.info({
        title: '시즌 비디오 URL 새로고침 결과',
        width: 800,
        maskClosable: false,
        closable: true,
        content: (
          <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
            <div
              style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f6f6f6',
                borderRadius: '6px',
              }}
            >
              <strong>전체 결과: </strong>
              시즌에 속한 {results.length}개의 컨텐츠에 대해 비디오 URL 새로고침을 실행했습니다.
            </div>
            <div style={{ marginBottom: '16px' }}>
              <span style={{ color: '#52c41a', fontWeight: 'bold' }}>성공: {totalSuccess}개</span>
              {' | '}
              <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>실패: {totalFailed}개</span>
            </div>

            {results.map((detail: VideoManifestUpdateDetailInterface, index: number) => (
              <div
                key={index}
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  backgroundColor: detail.success ? '#f6ffed' : '#fff2f0',
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <strong>콘텐츠:</strong> {detail.content_title} (ID: {detail.content_id})
                </div>
                <div
                  style={{
                    marginBottom: '8px',
                    color: detail.success ? '#52c41a' : '#ff4d4f',
                    fontWeight: 'bold',
                  }}
                >
                  상태: {detail.success ? '✅ 성공' : '❌ 실패'}
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>메시지:</strong> {detail.message}
                </div>

                {detail.updated_manifests && detail.updated_manifests.length > 0 && (
                  <div>
                    <strong>업데이트된 매니페스트:</strong>
                    {detail.updated_manifests.map(
                      (manifest: UpdatedManifestInterface, idx: number) => (
                        <div
                          key={idx}
                          style={{
                            marginTop: '8px',
                            padding: '8px',
                            backgroundColor: '#fafafa',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#666',
                          }}
                        >
                          <div>
                            <strong>해상도:</strong> {manifest.preset}
                          </div>
                          <div style={{ marginTop: '4px' }}>
                            <strong>이전 URL:</strong>
                            <div style={{ wordBreak: 'break-all', color: '#666' }}>
                              {manifest.old_url}
                            </div>
                          </div>
                          <div style={{ marginTop: '4px' }}>
                            <strong>새 URL:</strong>
                            <div style={{ wordBreak: 'break-all', color: '#1890ff' }}>
                              {manifest.new_url}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ),
      });
    };

    Modal.confirm({
      title: '시즌 비디오 URL 새로고침',
      content: '시즌에 속한 모든 컨텐츠의 ODK로부터 HLS 정보를 다시 가져옵니다. 실행하시겠습니까?',
      onOk: async () => {
        setIsRefreshing(true);

        try {
          // 새로운 Season Content Link API 사용 - Season에 매핑된 content만 조회
          const seasonContentsResponse = await seasonContentService.getSeasonContents(seasonId);

          if (seasonContentsResponse.contents.length === 0) {
            message.info('시즌에 속한 컨텐츠가 없습니다.');
            return;
          }

          const results: VideoManifestUpdateDetailInterface[] = [];
          let totalSuccess = 0;
          let totalFailed = 0;

          // 각 컨텐츠에 대해 비디오 URL 새로고침 실행
          for (const content of seasonContentsResponse.contents) {
            try {
              const result = await contentService.updateVideoManifest(content.id!);
              // 단일 컨텐츠 결과를 시즌 결과 형태로 변환
              if (result.details && result.details.length > 0) {
                results.push(...result.details);
                totalSuccess += result.success;
                totalFailed += result.failed;
              } else {
                // 상세 정보가 없는 경우 기본 정보로 생성
                results.push({
                  content_id: content.id!,
                  content_title: content.title,
                  success: true,
                  message: result.message || '성공',
                  updated_manifests: [],
                  created_manifests: [],
                });
                totalSuccess += 1;
              }
            } catch (error: unknown) {
              // 400 오류일 때는 응답 데이터를 파싱해서 결과에 추가
              if (
                error &&
                typeof error === 'object' &&
                'response' in error &&
                error.response &&
                typeof error.response === 'object' &&
                'status' in error.response &&
                error.response.status === 400 &&
                'data' in error.response
              ) {
                const errorData = error.response.data as VideoManifestUpdateResponseInterface;
                if (errorData.details && errorData.details.length > 0) {
                  results.push(...errorData.details);
                  totalSuccess += errorData.success;
                  totalFailed += errorData.failed;
                }
              } else {
                // 기타 오류는 실패로 처리
                results.push({
                  content_id: content.id!,
                  content_title: content.title,
                  success: false,
                  message:
                    error && typeof error === 'object' && 'message' in error
                      ? String(error.message)
                      : '알 수 없는 오류',
                  updated_manifests: [],
                  created_manifests: [],
                });
                totalFailed += 1;
              }
            }
          }

          showResultModal(results, totalSuccess, totalFailed);
        } catch (error: unknown) {
          console.error(error);
          message.error('시즌 비디오 URL 새로고침 중 오류가 발생했습니다.');
        } finally {
          setIsRefreshing(false);
        }
      },
    });
  };

  // 시즌의 모든 컨텐츠에 대해 Retranscode with embedded subtitle을 실행하는 함수
  const handleSeasonRetranscodeWithEmbeddedSubtitle = async (): Promise<void> => {
    // 결과 모달을 표시하는 공통 함수
    const showResultModal = (
      results: { content: SeasonContentInterface; success: boolean; error?: string }[],
      totalSuccess: number,
      totalFailed: number
    ) => {
      Modal.info({
        title: '시즌 Retranscode with Embedded Subtitle 결과',
        width: 800,
        maskClosable: false,
        closable: true,
        content: (
          <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
            <div
              style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f6f6f6',
                borderRadius: '6px',
              }}
            >
              <strong>전체 결과: </strong>총 {results.length}개 콘텐츠 중 {totalSuccess}개 성공,{' '}
              {totalFailed}개 실패
            </div>
            <div style={{ marginBottom: '16px' }}>
              <span style={{ color: '#52c41a', fontWeight: 'bold' }}>성공: {totalSuccess}개</span>
              {' | '}
              <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>실패: {totalFailed}개</span>
            </div>

            {results.map((result, index: number) => (
              <div
                key={index}
                style={{
                  marginBottom: '20px',
                  padding: '16px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  backgroundColor: result.success ? '#f6ffed' : '#fff2f0',
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <strong>콘텐츠:</strong> {result.content.title} (ID: {result.content.id})
                </div>
                <div
                  style={{
                    marginBottom: '8px',
                    color: result.success ? '#52c41a' : '#ff4d4f',
                    fontWeight: 'bold',
                  }}
                >
                  상태: {result.success ? '✅ 성공' : '❌ 실패'}
                </div>
                {result.error && (
                  <div style={{ marginBottom: '12px', color: '#ff4d4f' }}>
                    <strong>에러:</strong> {result.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        ),
      });
    };

    Modal.confirm({
      title: '시즌 Retranscode with Embedded Subtitle',
      content:
        '시즌에 속한 모든 컨텐츠에 대해 embedded subtitle과 함께 재트랜스코딩을 실행합니다. 실행하시겠습니까?',
      onOk: async () => {
        setIsRetranscoding(true);

        try {
          // 새로운 Season Content Link API 사용 - Season에 매핑된 content만 조회
          const seasonContentsResponse = await seasonContentService.getSeasonContents(seasonId);

          if (seasonContentsResponse.contents.length === 0) {
            message.info('시즌에 속한 컨텐츠가 없습니다.');
            return;
          }

          const results: {
            content: SeasonContentInterface;
            success: boolean;
            error?: string;
          }[] = [];
          let totalSuccess = 0;
          let totalFailed = 0;

          // 각 컨텐츠에 대해 retranscode with embedded subtitle 실행
          for (const content of seasonContentsResponse.contents) {
            try {
              await contentService.videoTranscodeWithEmbeddedSubtitle(content.id);
              results.push({
                content,
                success: true,
              });
              totalSuccess++;
            } catch (error: any) {
              let errorMessage = '알 수 없는 오류';
              if (error?.response?.data) {
                errorMessage = String(error.response.data);
              } else if (error?.message) {
                errorMessage = error.message;
              }

              results.push({
                content,
                success: false,
                error: errorMessage,
              });
              totalFailed++;
            }
          }

          showResultModal(results, totalSuccess, totalFailed);

          if (totalSuccess > 0) {
            message.success(
              `${totalSuccess}개 콘텐츠에 대해 retranscode with embedded subtitle이 시작되었습니다.`
            );
          }
        } catch (error: any) {
          console.error('시즌 retranscode with embedded subtitle 실행 중 오류:', error);
          message.error('시즌 컨텐츠 목록을 가져오는 중 오류가 발생했습니다.');
        } finally {
          setIsRetranscoding(false);
        }
      },
    });
  };

  const columns = [
    {
      title: 'THUMBNAIL',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      width: '5%',
      render: (_text: string, record: ContentInterface): JSX.Element => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            style={{ marginLeft: '10px', width: '100%', height: 'auto' }}
            src={record.thumbnail?.file ?? ''}
            alt=""
          />
        </div>
      ),
    },
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      width: '40%',
      render: (text: string, record: ContentInterface): JSX.Element => (
        <div className="content-title" onClick={() => handleOnClickTitle(record.id || 0)}>
          <div
            className="title-content"
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </div>
        </div>
      ),
    },
    {
      title: 'status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Episode Order',
      dataIndex: 'episode_order',
      key: 'episode_order',
    },
    {
      title: 'created (UTC)',
      dataIndex: 'created',
      key: 'created',
      render: (text: string, _: ContentInterface): JSX.Element => (
        <span>{moment(text).format('YYYY-MM-DD HH:mm:ss')}</span>
      ),
    },
  ];

  const fetchContent = useCallback(
    async (params: URLSearchParams): Promise<{ data: ContentInterface[]; count: number }> => {
      try {
        // 새로운 Season Content Link API 사용 - Season에 매핑된 content만 조회
        const seasonContents = await seasonContentService.getSeasonContents(seasonId);
        console.log('VIEW MODE - seasonContents:', seasonContents);

        // clip/trailer 타입은 has_additional_link가 true인 것만 필터링
        const validContents = seasonContents.contents.filter(content => {
          if (content.content_type === 'clip' || content.content_type === 'trailer') {
            return content.has_additional_link === true;
          }
          return true; // 다른 타입들은 모두 표시
        });

        // SeasonContentInterface를 ContentInterface로 변환
        const contentData: ContentInterface[] = validContents.map(
          (content: SeasonContentInterface) => ({
            id: content.id,
            title: content.title,
            title_en: content.title_en,
            content_type: content.content_type as any,
            slug: content.slug,
            status: content.status as any,
            created: content.created,
            modified: content.modified,
            episode_order: content.episode_order, // API에서 제공하는 episode_order 값 사용
            ingest_source: content.ingest_source ?? null, // ingest_source 필드 추가 (null 체크)
            // 기타 필수 필드들은 기본값으로 설정
            thumbnail: { id: 0, uid: '', file: undefined, is_representative: false },
            season: seasonId,
            cast: [],
            categories: [],
            contract: 0,
            mature_content: false,
            on_air: false,
            age_rating: AGE_RATING[0],
            dubbing_mode: '',
            contents_file: { file_name: '' },
            content_stream_manifests: [],
            platform_id: 0,
          })
        );

        // 검색 및 필터링 (클라이언트 사이드)
        let filteredData = contentData;

        // 제목으로 검색
        const searchTerm = params.get('search');
        if (searchTerm) {
          const search = searchTerm.toLowerCase();
          filteredData = filteredData.filter(
            content =>
              content.title.toLowerCase().includes(search) ||
              content.title_en?.toLowerCase().includes(search)
          );
        }

        // 상태 필터링 (기본적으로 published만 보여주지만 파라미터로 변경 가능)
        const statusFilter = params.get('status') || 'published';
        if (statusFilter !== 'all') {
          filteredData = filteredData.filter(content => content.status === statusFilter);
        }

        return { data: filteredData, count: filteredData.length };
      } catch (error) {
        console.error('Season content 조회 실패:', error);
        return { data: [], count: 0 };
      }
    },
    [seasonId]
  );

  const transferColumns: TableColumnsType<TransferDataSourceInterface> = [
    {
      dataIndex: 'id',
      title: 'ID',
      render: (id: number) => (
        <span>
          <Link to={`/video/detail/${id}`} target="_blank">
            {id}
          </Link>
        </span>
      ),
    },
    {
      dataIndex: 'name',
      title: 'Name',
    },
    {
      dataIndex: 'content_type',
      title: 'Content Type',
      filters: [
        {
          text: 'Tv-series',
          value: 'tv-series',
        },
        {
          text: 'Movie',
          value: 'movie',
        },
        {
          text: 'Clip',
          value: 'clip',
        },
        {
          text: 'Trailer',
          value: 'trailer',
        },
        {
          text: 'Live-Streaming',
          value: 'live_streaming',
        },
      ],
    },
    {
      dataIndex: 'episode_order',
      title: 'Episode Order',
    },
    {
      dataIndex: 'status',
      title: 'Status',
    },
  ];

  const dataHandler: TransferDataHandlerInterface = {
    fetchSourceData: async (
      query: Record<string, string>
    ): Promise<{ data: TransferDataSourceInterface[]; count: number }> => {
      const extendedQuery = new URLSearchParams(query);
      extendedQuery.append('order_by', 'episode_order');
      const contents = await contentService.fetch(extendedQuery);
      const transferDataSource = contents.results.map(content => ({
        id: content.id,
        key: content.id,
        name: content.title,
        content_type: content.content_type,
        episode_order: content.episode_order,
        disabled: false,
        status: content.status,
      }));
      return {
        data: transferDataSource,
        count: contents.count,
      };
    },
    fetchTargetData: async (
      query: Record<string, string>
    ): Promise<{ data: TransferDataSourceInterface[]; count: number }> => {
      console.log('🎯 fetchTargetData CALLED with query:', query);
      try {
        // 새로운 Season Content Link API 사용 - Season에 연결된 content만 조회
        const seasonContents = await seasonContentService.getSeasonContents(seasonId);
        console.log('🎯 fetchTargetData - seasonContents:', seasonContents);
        console.log('🎯 fetchTargetData - query:', query);

        // 검색 필터링 (클라이언트 사이드)
        let filteredContents = seasonContents.contents;
        // clip/trailer 타입은 has_additional_link가 true인 것만 표시
        console.log('fetchTargetData - before clip/trailer filtering:', filteredContents.length);
        filteredContents = filteredContents.filter(content => {
          if (content.content_type === 'clip' || content.content_type === 'trailer') {
            console.log(
              `fetchTargetData - ${content.content_type} ${content.title}:
              has_additional_link = ${content.has_additional_link}`
            );
            return content.has_additional_link === true;
          }
          return true; // 다른 타입들은 모두 표시
        });
        console.log('fetchTargetData - after clip/trailer filtering:', filteredContents.length);

        // 제목으로 검색 필터링
        if (query.search) {
          const searchTerm = query.search.toLowerCase();
          filteredContents = filteredContents.filter(
            content =>
              content.title.toLowerCase().includes(searchTerm) ||
              content.title_en?.toLowerCase().includes(searchTerm)
          );
        }

        // 콘텐츠 타입으로 필터링 - Season Content에서는 모든 타입 허용
        // Season에 이미 연결된 content만 조회하므로 추가 타입 필터링 불필요
        console.log('fetchTargetData - content_type filtering skipped for Season Content');
        // 원래 필터링 로직 (주석 처리)
        // if (query.content_type && query.content_type !== 'all') {
        //   const allowedTypes = query.content_type.split(',');
        //   filteredContents = filteredContents.filter(content =>
        //     allowedTypes.includes(content.content_type)
        //   );
        // }

        // 상태로 필터링
        if (query.status) {
          filteredContents = filteredContents.filter(content => content.status === query.status);
        }

        const result = {
          data: filteredContents.map((content: SeasonContentInterface) => ({
            id: content.id,
            key: content.id,
            name: content.title,
            content_type: content.content_type,
            episode_order: content.episode_order, // API에서 제공하는 episode_order 값 사용
            disabled: false,
            status: content.status,
          })),
          count: filteredContents.length,
        };
        console.log('fetchTargetData - final result:', result);
        return result;
      } catch (error) {
        console.error('Season content 조회 실패:', error);
        return { data: [], count: 0 };
      }
    },
    createWithParentId: async (itemIds: number[]): Promise<void> => {
      try {
        await seasonContentService.addContentsToSeason(seasonId, itemIds);
        // message는 seasonContentService에서 이미 처리됨
      } catch (error) {
        console.error('Season에 content 추가 실패:', error);
        // 에러 메시지도 seasonContentService에서 이미 처리됨
        throw error;
      }
    },

    deleteWithParentId: async (itemIds: number[]): Promise<void> => {
      try {
        await seasonContentService.removeContentsFromSeason(seasonId, itemIds);
        // message는 seasonContentService에서 이미 처리됨
      } catch (error) {
        console.error('Season에서 content 제거 실패:', error);
        // 에러 메시지도 seasonContentService에서 이미 처리됨
        throw error;
      }
    },
  };

  return (
    <>
      <div
        style={{
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Switch
          checkedChildren="Edit Mode"
          unCheckedChildren="View Mode"
          onChange={(checked: boolean) => setEditMode(checked)}
          checked={editMode}
        />
        {!editMode && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button type="primary" onClick={handleSeasonVideoUrlRefresh} loading={isRefreshing}>
              시즌 비디오 URL 새로고침
            </Button>
            <Button
              type="default"
              onClick={handleSeasonRetranscodeWithEmbeddedSubtitle}
              loading={isRetranscoding}
            >
              시즌 Retranscode with Embedded Subtitle
            </Button>
          </div>
        )}
      </div>
      {editMode ? (
        <>
          <Flex align="start" gap="middle" vertical>
            <Component.TransferComponent dataHandler={dataHandler} columns={transferColumns} />
          </Flex>
        </>
      ) : (
        <Component.TableComponent<ContentInterface>
          name="Season Contents"
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          dataFetch={fetchContent}
          columns={columns}
        />
      )}
    </>
  );
};

export default observer(Content);
